import { ChangeDetectionStrategy, Component, Input, OnInit, inject } from '@angular/core'
import { FieldNameComponent } from '@shared/field-name/field-name.component'
import { TbButtonComponent } from '@components-library/tb-button/tb-button.component'
import { TbMenuComponent } from '@components-library/tb-menu'
import { TranslocoDirective } from '@jsverse/transloco'
import { TbTooltipComponent } from '@components-library/tb-tooltip/tb-tooltip-component/tb-tooltip.component'
import { EnvDirective } from '@shared/env.directive'
import { MatDialog } from '@angular/material/dialog'
import { FieldTypeFacadeService, UserFacadeService } from '@services/store-facade'
import { RecordActionPanelService } from '@app/feature/record-actions-panel/record-action-panel.service'
import { ViewDataService } from '@app/views/services/view-data.service'
import { RecordsService } from '@services/records.service'
import { BreakpointService, DisplaySize } from '@components-library/services/breakpoint.service'
import { UiFacadeService } from '@services/store-facade/ui-facade.service'
import { AutomationButtonsComponent } from '@app/feature/record-actions-panel/record-action-buttons/automation-buttons/automation-buttons.component'
import { MainBusinessRecord, CellEntities, Field, FieldType, RecordUpdate } from '@models/ui'
import { Dictionary } from '@ngrx/entity'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import {
    DialogGroupCellEditInput,
    DialogGroupCellEditOutput,
    GroupCellEditComponent,
} from '@app/feature/record-actions-panel/group-cell-edit/group-cell-edit.component'
import { without } from 'lodash-es'

@UntilDestroy()
@Component({
    selector: 'app-record-action-buttons',
    imports: [
        FieldNameComponent,
        TbButtonComponent,
        TbMenuComponent,
        TranslocoDirective,
        TbTooltipComponent,
        EnvDirective,
        AutomationButtonsComponent,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './record-action-buttons.component.html',
})
export class RecordActionButtonsComponent implements OnInit {
    dialog = inject(MatDialog)
    private readonly actionPanelService = inject(RecordActionPanelService)
    private readonly viewDataService = inject(ViewDataService)
    private readonly recordsService = inject(RecordsService)
    private readonly fieldTypeFacadeService = inject(FieldTypeFacadeService)
    private readonly userFacadeService = inject(UserFacadeService)
    private readonly breakpointService = inject(BreakpointService)
    private readonly uiFacadeService = inject(UiFacadeService)

    @Input({ required: true }) selectedRecords!: MainBusinessRecord[]
    @Input() dark = false

    fieldsList!: Field[]
    cells!: { [recordGuid: string]: CellEntities }
    fieldTypes!: Dictionary<FieldType>
    currentUserGuid!: string

    displayTooltip = false

    ngOnInit() {
        this.displayTooltip = this.isShowTooltip(this.breakpointService.displaySize)

        this.breakpointService.displaySize$.subscribe((size) => {
            this.displayTooltip = this.isShowTooltip(size)
        })

        this.viewDataService.viewDataChanges$.pipe(untilDestroyed(this)).subscribe(() => {
            this.cells = this.viewDataService.getCells()
            this.fieldsList = Object.values(this.viewDataService.getViewData().fields)
                .filter((field) => !field.operation_code_entity?.system)
                .filter((field) => !(field.system_name === 'name'))
        })

        this.fieldTypeFacadeService.selectFieldTypeEntities$
            .pipe(untilDestroyed(this))
            .subscribe((fieldTypes) => {
                this.fieldTypes = fieldTypes
            })

        this.userFacadeService.currentUser$.pipe(untilDestroyed(this)).subscribe((user) => {
            if (!user) return

            this.currentUserGuid = user.guid
        })
    }

    deleteAllRecords() {
        this.uiFacadeService
            .wrapRequestWithViewLoader(this.recordsService.deleteRecord(this.selectedRecords))
            .subscribe()
        this.actionPanelService.deselectAll()
    }

    setFavorite() {
        this.uiFacadeService
            .wrapRequestWithViewLoader(
                this.recordsService.updateRecords(
                    this.toggleFavorites(this.selectedRecords, this.currentUserGuid),
                ),
            )
            .subscribe()
        this.actionPanelService.deselectAll()
    }

    clearAllSelected() {
        this.actionPanelService.deselectAll()
    }

    editFieldValues(field: Field) {
        const dialogRef = this.dialog.open<
            GroupCellEditComponent,
            DialogGroupCellEditInput,
            DialogGroupCellEditOutput | undefined
        >(GroupCellEditComponent, {
            minWidth: '400px',
            data: {
                records: this.selectedRecords,
                field: field,
                fieldTypes: this.fieldTypes,
                cells: this.cells,
            },
        })

        dialogRef.afterClosed().subscribe((res: DialogGroupCellEditOutput | undefined) => {
            if (res && res.value) {
                this.editRecords({ value: res.value, field: res.field })
            }
        })
    }

    editRecords(dataRecord: { value: string; field: Field }) {
        let data = this.selectedRecords.map((record) => {
            return {
                record: record,
                cell: this.cells[record.guid][dataRecord.field.guid],
                value: dataRecord.value,
            }
        })

        this.uiFacadeService
            .wrapRequestWithViewLoader(this.recordsService.updateRecords(data))
            .subscribe()
        this.clearAllSelected()
    }

    private toggleFavorites(records: MainBusinessRecord[], userGuid: string) {
        const recordWithoutWatch = !!records.find(
            (record) => !this.recordsService.isFavorite(record.watch.value, userGuid),
        )

        if (recordWithoutWatch) {
            return this.updateWithAllSetWatches(records, userGuid)
        }

        return this.updateWithNonSetWatches(records, userGuid)
    }

    private updateWithNonSetWatches(records: MainBusinessRecord[], userGuid: string) {
        return records.map((record) => {
            const watchValues = record.watch.value.split(',')
            return {
                record,
                cell: record.watch,
                value: without(watchValues, userGuid).join(','),
            }
        })
    }

    private updateWithAllSetWatches(
        records: MainBusinessRecord[],
        userGuid: string,
    ): RecordUpdate[] {
        return records.map((record) => {
            if (this.recordsService.isFavorite(record.watch.value, userGuid)) {
                return {
                    record,
                    cell: record.watch,
                    value: record.watch.value,
                }
            }

            return {
                record,
                cell: record.watch,
                value: record.watch.value.length ? `${record.watch.value}, ${userGuid}` : userGuid,
            }
        })
    }

    private isShowTooltip(size: DisplaySize) {
        return size === DisplaySize.Small || size === DisplaySize.Medium
    }
}
